import api from '../services/api';
import { createContext, useContext, useState, useCallback, useRef } from 'react';

const ChannelPartnerContext = createContext();

export const useChannelPartner = () => useContext(ChannelPartnerContext);

export const ChannelPartnerProvider = ({ children }) => {
    const [channelPartners, setChannelPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const fetchingPartnersRef = useRef(new Set()); // Track ongoing fetches by ID

    const fetchChannelPartners = async () => {
        try {
            setLoading(true);
            const response = await api.get('/lms/channel-partners/');
            if (response.data?.success) {
                setChannelPartners(response.data.data);
                return response.data.data;
            } else {
                throw new Error(response.data?.message || 'Failed to fetch channel partners');
            }
        } catch (error) {
            console.error('Error fetching channel partners:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const createChannelPartner = async (channelPartnerData) => {
        try {
            setLoading(true);
            const response = await api.post('/lms/channel-partners/', channelPartnerData);
            if (response.data?.success) {
                await fetchChannelPartners(); // Refresh the list
                return response.data;
            } else {
                throw new Error(response.data?.message || 'Failed to create channel partner');
            }
        } catch (error) {
            console.error('Error creating channel partner:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const updateChannelPartner = async (channelPartnerData) => {
        try {
            setLoading(true);
            const response = await api.put(`/lms/channel-partners/${channelPartnerData.id}/`, channelPartnerData);
            if (response.data?.success) {
                await fetchChannelPartners(); // Refresh the list
                return response.data;
            } else {
                throw new Error(response.data?.message || 'Failed to update channel partner');
            }
        } catch (error) {
            console.error('Error updating channel partner:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const deleteChannelPartner = async (channelPartnerId) => {
        try {
            setLoading(true);
            const response = await api.delete(`/lms/channel-partners/${channelPartnerId}/`);
            if (response.data?.success) {
                await fetchChannelPartners(); // Refresh the list
                return response.data;
            } else {
                throw new Error(response.data?.message || 'Failed to delete channel partner');
            }
        } catch (error) {
            console.error('Error deleting channel partner:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const updateChannelPartnerStatus = async (channelPartnerId, status) => {
        try {
            const response = await api.patch(`/lms/channel-partners/${channelPartnerId}/`, { status });
            if (response.data?.success) {
                // Update the local state immediately for better UX
                setChannelPartners(prev => 
                    prev.map(partner => 
                        partner.id === channelPartnerId 
                            ? { ...partner, status, status_verbose: status === 'active' ? 'Active' : 'Inactive' }
                            : partner
                    )
                );
                return response.data;
            } else {
                throw new Error(response.data?.message || 'Failed to update channel partner status');
            }
        } catch (error) {
            console.error('Error updating channel partner status:', error);
            throw error;
        }
    };

    const fetchChannelPartnerById = useCallback(async (channelPartnerId) => {
        // Prevent duplicate calls for the same ID
        if (fetchingPartnersRef.current.has(channelPartnerId)) {
            console.log(`Already fetching channel partner ${channelPartnerId}, skipping duplicate call`);
            return null;
        }

        fetchingPartnersRef.current.add(channelPartnerId);

        try {
            setLoading(true);
            const response = await api.get(`/lms/channel-partners/${channelPartnerId}/`);
            // The details API returns data directly, not wrapped in success/data
            if (response.data) {
                return response.data;
            } else {
                throw new Error('Failed to fetch channel partner details');
            }
        } catch (error) {
            console.error('Error fetching channel partner:', error);
            throw error;
        } finally {
            setLoading(false);
            fetchingPartnersRef.current.delete(channelPartnerId);
        }
    }, []);

    const value = {
        channelPartners,
        setChannelPartners,
        loading,
        fetchChannelPartners,
        fetchChannelPartnerById,
        createChannelPartner,
        updateChannelPartner,
        deleteChannelPartner,
        updateChannelPartnerStatus
    };

    return <ChannelPartnerContext.Provider value={value}>{children}</ChannelPartnerContext.Provider>;
};

export default ChannelPartnerContext;